import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronRight, ChevronLeft, Search, Type, FileText, CreditCard, Paperclip, Image as ImageIcon, Code, FunctionSquare, List, Download, Trash2, Bold, Italic, Underline } from 'lucide-react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import './style/NewDoc.css'


export default function Component() {
  const [title, setTitle] = useState('Page Title');
  const [isEditing, setIsEditing] = useState(false);
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(true);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(true);
  const [activeTool, setActiveTool] = useState(null);
  const [images, setImages] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [showFormatting, setShowFormatting] = useState(false);
  const [codeBlocks, setCodeBlocks] = useState([]);
  const [showTexEditor, setShowTexEditor] = useState(false);
  const [texFormula, setTexFormula] = useState('');
  
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const texEditorRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (editorRef.current && !editorRef.current.contains(e.target)) {
        setContextMenu(null);
      }
      if (texEditorRef.current && !texEditorRef.current.contains(e.target)) {
        closeTexEditor();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleLeftSidebar = () => setLeftSidebarOpen(!leftSidebarOpen);
  const toggleRightSidebar = () => setRightSidebarOpen(!rightSidebarOpen);

  const handleTitleClick = () => setIsEditing(true);
  const handleTitleChange = (e) => setTitle(e.target.value);
  const handleTitleBlur = () => setIsEditing(false);
  const handleToolClick = (tool) => setActiveTool(activeTool === tool ? null : tool);

  const handleEditorChange = () => {
    // We don't need to update any state here, as the contentEditable div manages its own content
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      document.execCommand('insertLineBreak');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
        
          let newWidth = img.width;
          let newHeight = img.height;
          
          if (newWidth > 300 || newHeight > 300) {
            if (newWidth > newHeight) {
              newHeight = (newHeight / newWidth) * 300;
              newWidth = 300;
            } else {
              newWidth = (newWidth / newHeight) * 300;
              newHeight = 300;
            }
          }
        
          canvas.width = newWidth;
          canvas.height = newHeight;
        
          ctx.drawImage(img, 0, 0, newWidth, newHeight);
        
          const resizedImage = canvas.toDataURL(file.type);
        
          const newImage = {
            id: Date.now(),
            src: resizedImage,
            originalSrc: event.target.result
          };
        
          setImages([...images, newImage]);
          
          // Insert the image at the current cursor position
          const selection = window.getSelection();
          const range = selection.getRangeAt(0);
          const fragment = range.createContextualFragment(`<img src="${resizedImage}" alt="Uploaded image ${newImage.id}" style="max-width: 300px; max-height: 300px;" />`);
          range.deleteContents();
          range.insertNode(fragment);
          range.collapse();
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    } else {
      alert('Please select an image file.');
    }
  };

  const triggerImageUpload = () => {
    if (editorRef.current) {
      editorRef.current.focus();
    }
    fileInputRef.current.click();
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    const selectedNode = window.getSelection().anchorNode;
    const clickedElement = selectedNode.nodeType === 1 ? selectedNode : selectedNode.parentElement;
    
    if (clickedElement.tagName === 'IMG') {
      const imageId = clickedElement.alt.match(/\d+/)[0];
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        imageId: imageId
      });
    }
  };

  const downloadOriginalImage = (imageId) => {
    const image = images.find(img => img.id === parseInt(imageId));
    if (image) {
      const link = document.createElement('a');
      link.href = image.originalSrc;
      link.download = `original_image_${imageId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    setContextMenu(null);
  };

  const deleteImage = (imageId) => {
    setImages(images.filter(img => img.id !== parseInt(imageId)));
    const imgToDelete = editorRef.current.querySelector(`img[alt="Uploaded image ${imageId}"]`);
    if (imgToDelete) {
      imgToDelete.remove();
    }
    setContextMenu(null);
  };

  const applyTextFormat = (format) => {
    document.execCommand(format, false, null);
  };

  const handleSelectionChange = () => {
    const selection = window.getSelection();
    setShowFormatting(selection.toString().length > 0);
  };

  const addCodeBlock = () => {
    const newCodeBlock = {
      id: Date.now(),
      language: 'javascript',
      code: '// Start coding here'
    };
    setCodeBlocks(prevBlocks => [...prevBlocks, newCodeBlock]);
    const codeBlockPlaceholder = `<div data-code-block-id="${newCodeBlock.id}">[Code Block]</div>`;
    document.execCommand('insertHTML', false, codeBlockPlaceholder);
  };

  const handleLanguageChange = (id, language) => {
    setCodeBlocks(prevBlocks => 
      prevBlocks.map(block => 
        block.id === id ? { ...block, language } : block
      )
    );
  };

  const handleCodeChange = useCallback((id, newCode) => {
    setCodeBlocks(prevBlocks => 
      prevBlocks.map(block => 
        block.id === id ? { ...block, code: newCode } : block
      )
    );
  }, []);

  const openTexEditor = () => {
    setShowTexEditor(true);
  };

  const closeTexEditor = () => {
    setShowTexEditor(false);
    setTexFormula('');
  };

  const handleTexFormulaChange = (e) => {
    setTexFormula(e.target.value);
  };

  const insertTexFormula = () => {
    try {
      const renderedFormula = katex.renderToString(texFormula, {
        throwOnError: false,
        displayMode: true,
      });
      const formulaId = Date.now();
      const formulaHTML = `<span class="tex-formula" data-formula-id="${formulaId}">${renderedFormula}</span>`;
    
      // Insert the formula at the current cursor position
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      const fragment = range.createContextualFragment(formulaHTML);
      range.deleteContents();
      range.insertNode(fragment);
      range.collapse();
    
      closeTexEditor();
    } catch (error) {
      console.error('Error rendering TeX formula:', error);
      alert('Error rendering TeX formula. Please check your input.');
    }
  };

  const focusTextArea = () => {
    if (editorRef.current) {
      editorRef.current.focus();
    }
  };

  const CodeBlock = React.memo(({ block, onLanguageChange, onCodeChange }) => {
    const textareaRef = useRef(null);
    const [localCode, setLocalCode] = useState(block.code);

    useEffect(() => {
      setLocalCode(block.code);
    }, [block.code]);

    const handleLocalCodeChange = (e) => {
      const newCode = e.target.value;
      setLocalCode(newCode);
    };

    const handleBlur = () => {
      onCodeChange(block.id, localCode);
    };

    return (
      <div className="code-block-wrapper">
        <div className="code-block-header">
          <select 
            className="code-language-select"
            value={block.language}
            onChange={(e) => onLanguageChange(block.id, e.target.value)}
          >
            <option value="javascript">JavaScript</option>
            <option value="python">Python</option>
            <option value="java">Java</option>
          </select>
        </div>
        <div className="code-editor">
          <div className="language-display">
            {block.language.charAt(0).toUpperCase() + block.language.slice(1)}
          </div>
          <textarea
            ref={textareaRef}
            className="code-textarea"
            value={localCode}
            onChange={handleLocalCodeChange}
            onBlur={handleBlur}
          />
        </div>
      </div>
    );
  });

  const insertOptions = [
    { icon: Type, label: 'Text', action: focusTextArea },
    { icon: FileText, label: 'Page', action: () => {} },
    { icon: CreditCard, label: 'Card', action: () => {} },
    { icon: Paperclip, label: 'File Attachment', action: () => {} },
    { icon: ImageIcon, label: 'Image', action: triggerImageUpload },
    { icon: Code, label: 'Code Block', action: addCodeBlock },
    { icon: FunctionSquare, label: 'TeX Formula', action: openTexEditor },
  ];
  return (
    <div className="doc-wrapper">
      <div className="doc-container">
        <div className={`doc-left-sidebar ${leftSidebarOpen ? 'open' : 'closed'}`}>
          {leftSidebarOpen && (
            <>
              <div className="doc-sidebar-tools">
                <button className={`doc-tool-button ${activeTool === 'toc' ? 'active' : ''}`} onClick={() => handleToolClick('toc')}>
                  <List />
                </button>
                <button className={`doc-tool-button ${activeTool === 'attachments' ? 'active' : ''}`} onClick={() => handleToolClick('attachments')}>
                  <Paperclip />
                </button>
                <button className={`doc-tool-button ${activeTool === 'search' ? 'active' : ''}`} onClick={() => handleToolClick('search')}>
                  <Search />
                </button>
              </div>
              {activeTool && (
                <div className="doc-tool-content">
                  <h3>{activeTool === 'toc' ? 'Table of Contents' : activeTool === 'attachments' ? 'Attachments' : 'Search'}</h3>
                  <p>{activeTool === 'toc' ? 'Use titles, pages or cards to create a table of contents.' : activeTool === 'attachments' ? 'No attachments yet.' : 'Search your document...'}</p>
                </div>
              )}
            </>
          )}
        </div>
        <button className={`doc-toggle-sidebar left ${leftSidebarOpen ? 'open' : 'closed'}`} onClick={toggleLeftSidebar}>
          {leftSidebarOpen ? <ChevronLeft /> : <ChevronRight />}
        </button>
        <div className="doc-main-content">
          {isEditing ? (
            <input
              type="text"
              value={title}
              onChange={handleTitleChange}
              onBlur={handleTitleBlur}
              autoFocus
              className="doc-title-input"
            />
          ) : (
            <h1 onClick={handleTitleClick}>{title}</h1>
          )}
          <div className={`doc-toolbar ${showFormatting ? 'visible' : ''}`}>
            <button onClick={() => applyTextFormat('bold')}><Bold size={16} /></button>
            <button onClick={() => applyTextFormat('italic')}><Italic size={16} /></button>
            <button onClick={() => applyTextFormat('underline')}><Underline size={16} /></button>
          </div>
          <div 
            ref={editorRef}
            className="doc-editor-area" 
            contentEditable={true}
            onInput={handleEditorChange}
            onKeyDown={handleKeyDown}
            onContextMenu={handleContextMenu}
            onMouseUp={handleSelectionChange}
            onKeyUp={handleSelectionChange}
          />
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleImageUpload}
            accept="image/*"
          />
          {codeBlocks.map(block => (
            <CodeBlock
              key={block.id}
              block={block}
              onLanguageChange={handleLanguageChange}
              onCodeChange={handleCodeChange}
            />
          ))}
        </div>
        <div className={`doc-right-sidebar ${rightSidebarOpen ? 'open' : 'closed'}`}>
          {rightSidebarOpen && (
            <div className="doc-insert-options">
              <h3>Insert</h3>
              <p>Click any item to insert into the document</p>
              <ul>
                {insertOptions.map((item, index) => (
                  <li key={index} onClick={item.action}>
                    <item.icon />
                    <span>{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <button className={`doc-toggle-sidebar right ${rightSidebarOpen ? 'open' : 'closed'}`} onClick={toggleRightSidebar}>
          
          {rightSidebarOpen ? <ChevronRight /> : <ChevronLeft />}
        </button>
      </div>
      {contextMenu && (
        <div className="doc-context-menu" style={{ top: contextMenu.y, left: contextMenu.x }}>
          <button onClick={() => downloadOriginalImage(contextMenu.imageId)}><Download size={16} /> Download Original</button>
          <button onClick={() => deleteImage(contextMenu.imageId)}><Trash2 size={16} /> Delete Image</button>
        </div>
      )}
      {showTexEditor && (
        <div className="tex-editor-overlay">
          <div className="tex-editor-container" ref={texEditorRef}>
            <h3>Insert TeX Formula</h3>
            <textarea
              value={texFormula}
              onChange={handleTexFormulaChange}
              placeholder="Enter your TeX formula here..."
            />
            <div className="tex-editor-buttons">
              <button onClick={insertTexFormula}>Insert</button>
              <button onClick={closeTexEditor}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}